// src/components/AutoCUPAssistant.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeKey(value) {
  // Produit des espaces simples — aligné avec artkiv_normalize_key() côté Postgres
  // (qui pilote artist_quotes.artist_key via le trigger trg_artist_quotes_set_keys).
  // Ex: 'Françoise PÉTROVITCH' → 'francoise petrovitch', PAS 'francoise_petrovitch'.
  return normalizeText(value)
    .replace(/['’]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}


function getCareerMarketWeight(targetTier, comparableTier, sameArtist = false) {
  if (sameArtist) return 1;

  const weights = {
    emerging: { emerging: 0.8, "mid-career": 0.5, established: 0.3, "blue-chip": 0.15 },
    "mid-career": { emerging: 0.7, "mid-career": 0.8, established: 0.5, "blue-chip": 0.25 },
    established: { emerging: 0.5, "mid-career": 0.7, established: 0.8, "blue-chip": 0.4 },
    "blue-chip": { emerging: 0.3, "mid-career": 0.5, established: 0.7, "blue-chip": 0.9 },
  };

  return weights?.[targetTier]?.[comparableTier] ?? 0.25;
}

function getTechniqueMarketWeight(target, row) {
  if (
    target?.medium_normalized_key &&
    row?.medium_normalized_key &&
    target.medium_normalized_key === row.medium_normalized_key
  ) {
    return 1;
  }

  if (
    target?.medium_family_key &&
    row?.medium_family_key &&
    target.medium_family_key === row.medium_family_key
  ) {
    return 0.65;
  }

  return 0.25;
}


function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function localeFromLanguage(language) {
  const code = String(language || "fr").split("-")[0];
  if (code === "de") return "de-DE";
  if (code === "en") return "en-GB";
  return "fr-FR";
}

function formatEuro(value, locale = "fr-FR") {
  const n = toNumber(value);
  if (n === null) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function formatDate(value, locale) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString(locale);
}


function clampScore(value, min = 0, max = 100) {
  const n = toNumber(value);
  if (n === null) return min;
  return Math.max(min, Math.min(max, Math.round(n)));
}

const METHOD_GROUP_KEYS = {
  direct_cup: "directCup",
  analogy_cup: "analogyCup",
  point_method: "pointMethod",
  direct_cup_prudent: "directCupPrudent",
  analogy_cup_ratio: "analogyCupRatio",
  hybrid_points_cup: "hybridPointsCup",
};

function getMethodGroupLabel(methodGroup, t) {
  const key = METHOD_GROUP_KEYS[String(methodGroup || "").trim()];
  return key
    ? t(`autoCUPAssistant.methodGroups.${key}`)
    : t("autoCUPAssistant.methodGroups.unknown");
}

const MEDIUM_CANONICAL_KEYS = new Set([
  "painting",
  "sculpture",
  "photography",
  "drawing",
  "print",
  "mixed_media",
  "installation",
  "video",
  "textile",
  "ceramic",
  "nft",
  "other",
  "oil_on_canvas",
  "acrylic_on_canvas",
  "ink_on_paper",
  "watercolor_on_paper",
  "pencil_on_paper",
  "charcoal_on_paper",
  "mixed_media_on_paper",
]);

function humanizeMedium(value, t) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (MEDIUM_CANONICAL_KEYS.has(key)) {
    return t(`autoCUPAssistant.mediums.${key}`);
  }
  return raw.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatSupportRefined(value, t) {
  const key = String(value || "").trim();
  const known = new Set([
    "oil_canvas",
    "acrylic_canvas",
    "paper_mounted_canvas",
    "paper_work",
    "canvas",
  ]);
  return known.has(key) ? t(`autoCUPAssistant.supports.${key}`) : key;
}

function comparableTechniqueParts(row, t) {
  const family = humanizeMedium(row?.medium || "", t);
  const material = humanizeMedium(row?.material || "", t);
  const support = formatSupportRefined(row?.support_refined || "", t);
  return { family, material, support };
}

function median(values) {
  const clean = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!clean.length) return null;
  const mid = Math.floor(clean.length / 2);
  return clean.length % 2 ? clean[mid] : (clean[mid - 1] + clean[mid]) / 2;
}

function percentile(values, p) {
  const clean = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!clean.length) return null;
  const idx = Math.min(clean.length - 1, Math.max(0, Math.round((clean.length - 1) * p)));
  return clean[idx];
}

function weightedMedian(items) {
  const clean = items
    .filter((item) => Number.isFinite(item.value) && Number.isFinite(item.weight) && item.weight > 0)
    .sort((a, b) => a.value - b.value);

  if (!clean.length) return null;

  const totalWeight = clean.reduce((sum, item) => sum + item.weight, 0);
  let cumulative = 0;

  for (const item of clean) {
    cumulative += item.weight;
    if (cumulative >= totalWeight / 2) return item.value;
  }

  return clean[clean.length - 1].value;
}

function weightedPercentile(items, p) {
  const clean = items
    .filter((item) => Number.isFinite(item.value) && Number.isFinite(item.weight) && item.weight > 0)
    .sort((a, b) => a.value - b.value);

  if (!clean.length) return null;

  const totalWeight = clean.reduce((sum, item) => sum + item.weight, 0);
  const target = totalWeight * Math.min(1, Math.max(0, p));
  let cumulative = 0;

  for (const item of clean) {
    cumulative += item.weight;
    if (cumulative >= target) return item.value;
  }

  return clean[clean.length - 1].value;
}

function parseArtworkDimensions(artwork) {
  const specs = artwork?.specifications || {};
  const d = specs.dimensions || {};
  const h = toNumber(artwork?.height ?? artwork?.dimensions_height ?? d.height);
  const w = toNumber(artwork?.width ?? artwork?.dimensions_width ?? d.width);
  const depth = toNumber(artwork?.depth ?? artwork?.dimensions_depth ?? d.depth);
  return { h, w, depth };
}

function isLikelyUnsold(row) {
  const text = normalizeText(`${row.comment || ""} ${row.status || ""} ${row.result || ""}`);
  return text.includes("not sold") || text.includes("non vendu") || text.includes("invendu") || text.includes("unsold");
}

function getComparablePrice(row) {
  // Prix réel uniquement. Ne jamais prendre estimated_value comme prix de vente :
  // pour les lots invendus, estimated_value peut contenir le milieu de fourchette
  // et polluer la médiane. Les estimations restent affichables, pas calculantes.
  if (isLikelyUnsold(row)) return null;
  return (
    toNumber(row.price_with_premium) ||
    toNumber(row.buyer_premium_price) ||
    toNumber(row.premium_price) ||
    toNumber(row.hammer_price) ||
    null
  );
}

function rawComparablePrice(row) {
  return getComparablePrice(row);
}

function dimensionCoefficient(artworkDims, comp) {
  const targetH = artworkDims.h;
  const targetW = artworkDims.w;
  const compH = toNumber(comp.height_cm);
  const compW = toNumber(comp.width_cm);

  if (!targetH || !compH) return 0.6;

  const hRatio = Math.min(targetH, compH) / Math.max(targetH, compH);

  if (!targetW || !compW) {
    if (hRatio >= 0.85) return 1;
    if (hRatio >= 0.65) return 0.7;
    if (hRatio >= 0.45) return 0.4;
    return 0.25;
  }

  const wRatio = Math.min(targetW, compW) / Math.max(targetW, compW);
  const ratio = hRatio * 0.65 + wRatio * 0.35;

  if (ratio >= 0.85) return 1;
  if (ratio >= 0.65) return 0.7;
  if (ratio >= 0.45) return 0.4;
  return 0.25;
}

function dimensionScore(artworkDims, comp) {
  return Math.round(dimensionCoefficient(artworkDims, comp) * 25);
}

function marketFamilyTextFromArtwork(artwork) {
  // Priorité aux champs de famille de marché.
  // Le champ artwork.medium peut contenir une matière/support ("Huile sur papier"),
  // donc il ne vient qu'en dernier recours.
  return normalizeText([
    artwork?.medium_category,
    artwork?.medium_family_key,
    artwork?.typeOfArt,
    artwork?.specifications?.medium_category,
    artwork?.specifications?.typeOfArt,
    artwork?.medium,
  ].filter(Boolean).join(" "));
}

function materialTextFromArtwork(artwork) {
  // Technique, matériau et support de l'œuvre cible.
  return normalizeText([
    artwork?.medium,
    artwork?.medium_original,
    artwork?.medium_display_override,
    artwork?.medium_normalized_key,
    artwork?.specifications?.medium,
    artwork?.specifications?.material,
    artwork?.specifications?.support,
  ].filter(Boolean).join(" "));
}

function mediumTextFromArtwork(artwork) {
  return normalizeText([
    marketFamilyTextFromArtwork(artwork),
    materialTextFromArtwork(artwork),
  ].filter(Boolean).join(" "));
}

function marketFamilyTextFromComparable(row) {
  // Dans estimation_comparables, medium = famille marché après nettoyage.
  // material = technique / matière / support.
  return normalizeText([
    row?.medium,
    row?.medium_category,
    row?.medium_family_key,
    row?.typeOfArt,
  ].filter(Boolean).join(" "));
}

function materialTextFromComparable(row) {
  return normalizeText([
    row?.material,
    row?.support,
    row?.technique,
    row?.medium_normalized_key,
    row?.description,
  ].filter(Boolean).join(" "));
}

function mediumTextFromComparable(row) {
  return normalizeText([
    marketFamilyTextFromComparable(row),
    materialTextFromComparable(row),
  ].filter(Boolean).join(" "));
}

const MEDIUM_GROUPS = {
  painting: /painting|peinture|oil|huile|acrylic|acrylique|mixed media|technique mixte|tempera|casein|encaustic|gesso|pigment/,
  drawing: /drawing|dessin|watercolor|aquarelle|gouache|ink|encre|pastel|charcoal|fusain|pencil|crayon|lavis|wash/,
  print: /print|multiple|lithograph|lithographie|silkscreen|screenprint|serigraph|serigraphie|estampe|engraving|gravure|etching|eau forte|aquatint|woodcut|linocut|edition/,
  photography: /photo|photography|photographie|tirage|argentique|lambda|chromogenic|c print|gelatin|silver print|pigment print|inkjet|diasec/,
  sculpture: /sculpture|bronze|marble|marbre|stone|pierre|wood|bois|ceramic|ceramique|resin|resine|plaster|platre|metal|acier|steel|iron|fer|aluminium|volume|object|objet/,
  digital: /digital|nft|video|film|animation|generative|software|new media|media art/,
};

const SUPPORT_GROUPS = {
  canvas: /canvas|toile|linen|lin|cotton|coton/,
  paper: /paper|papier|cardboard|carton|arches|japon|wove|laid paper/,
  wood: /wood|bois|panel|panneau|board|isorel|mdf/,
  metal: /metal|acier|steel|iron|fer|aluminium|copper|cuivre|bronze/,
  plexi: /plexiglas|plexi|acrylic glass|diasec/,
  wall: /wall|mur|fresco|fresque/,
};

function detectedKeys(text, groups) {
  return Object.entries(groups)
    .filter(([, rx]) => rx.test(text || ""))
    .map(([key]) => key);
}

function primaryMediumFamily(text) {
  const hits = detectedKeys(text, MEDIUM_GROUPS);
  return hits[0] || "unknown";
}

function marketFamilyFromArtwork(artwork) {
  return primaryMediumFamily(marketFamilyTextFromArtwork(artwork) || materialTextFromArtwork(artwork));
}

function marketFamilyFromComparable(row) {
  return primaryMediumFamily(marketFamilyTextFromComparable(row) || materialTextFromComparable(row));
}

function marketFamiliesAreAutoCupCompatible(targetFamily, rowFamily) {
  if (!targetFamily || !rowFamily || targetFamily === "unknown" || rowFamily === "unknown") return true;
  if (targetFamily === rowFamily) return true;

  // Les œuvres sur papier peuvent parfois dialoguer avec dessin/estampe,
  // mais peinture, sculpture, photographie et digital ne doivent pas se contaminer.
  const compatiblePairs = new Set([
    "drawing|print",
    "print|drawing",
  ]);

  return compatiblePairs.has(`${targetFamily}|${rowFamily}`);
}

function isAutoCUPEligible(target, row) {
  const targetFamily = marketFamilyFromArtwork(target);
  const rowFamily = marketFamilyFromComparable(row);

  if (!marketFamiliesAreAutoCupCompatible(targetFamily, rowFamily)) {
    return false;
  }

  if (targetFamily !== "unknown" && rowFamily !== "unknown" && targetFamily === rowFamily) {
    return true;
  }

  return techniqueCoefficient(target, row) >= 0.8;
}

function primarySupport(text) {
  const hits = detectedKeys(text, SUPPORT_GROUPS);
  return hits[0] || "unknown";
}

function isMultipleEdition(row) {
  const med = mediumTextFromComparable(row);
  return MEDIUM_GROUPS.print.test(med)
    || /edition|ed\.?\s*\d+|tirage|multiple/.test(med)
    || (row.edition_total && Number(row.edition_total) > 1);
}

function isUniqueWork(artwork) {
  const med = mediumTextFromArtwork(artwork);
  return !MEDIUM_GROUPS.print.test(med) && !MEDIUM_GROUPS.photography.test(med);
}

function techniqueCoefficient(artwork, row) {
  const artworkMaterial = materialTextFromArtwork(artwork);
  const rowMaterial = materialTextFromComparable(row);
  const artworkMedium = mediumTextFromArtwork(artwork);
  const rowMedium = mediumTextFromComparable(row);

  if (!artworkMedium || !rowMedium) return 0.6;
  if (artworkMaterial && rowMaterial && (rowMaterial.includes(artworkMaterial) || artworkMaterial.includes(rowMaterial))) return 1;

  // Pénalité forte si l'œuvre cible est une pièce unique et le comparable un multiple/estampe.
  if (isUniqueWork(artwork) && isMultipleEdition(row)) return 0.25;

  const artworkFamily = marketFamilyFromArtwork(artwork);
  const rowFamily = marketFamilyFromComparable(row);
  const artworkSupport = primarySupport(artworkMaterial || artworkMedium);
  const rowSupport = primarySupport(rowMaterial || rowMedium);

  const sameFamily = artworkFamily !== "unknown" && artworkFamily === rowFamily;
  const sameSupport = artworkSupport !== "unknown" && artworkSupport === rowSupport;
  const knownSupportMismatch = artworkSupport !== "unknown" && rowSupport !== "unknown" && artworkSupport !== rowSupport;

  // Même technique précise ou quasi précise.
  const exactTechniquePairs = [
  [/gelatin silver print/, /gelatin silver print/],
  [/bromoil gelatin silver print/, /bromoil gelatin silver print/],
  [/silver gelatin/, /silver gelatin/],
    [/oil|huile/, /oil|huile/],
    [/acrylic|acrylique/, /acrylic|acrylique/],
    [/mixed media|technique mixte/, /mixed media|technique mixte/],
    [/gouache/, /gouache/],
    [/watercolor|aquarelle/, /watercolor|aquarelle/],
    [/ink|encre/, /ink|encre/],
    [/bronze/, /bronze/],
    [/marble|marbre/, /marble|marbre/],
    [/ceramic|ceramique/, /ceramic|ceramique/],
    [/lambda/, /lambda/],
    [/c print|chromogenic/, /c print|chromogenic/],
    [/lithograph|lithographie/, /lithograph|lithographie/],
    [/serigraph|serigraphie|screenprint|silkscreen/, /serigraph|serigraphie|screenprint|silkscreen/],
  ];

  const exactTechnique = exactTechniquePairs.some(([a, b]) => a.test(artworkMedium) && b.test(rowMedium));

  if (exactTechnique && sameSupport) return 1;
  if (exactTechnique && !knownSupportMismatch) return 0.95;

  // Peinture : support très important. Huile/toile ≠ huile/papier ≠ huile/bois.
  if (sameFamily && artworkFamily === "painting") {
    if (sameSupport) return 0.9;
    if (knownSupportMismatch) return 0.65;
    return 0.8;
  }

  // Dessin / œuvres sur papier : support papier attendu, technique secondaire importante.
  if (sameFamily && artworkFamily === "drawing") {
    if (sameSupport || artworkSupport === "unknown" || rowSupport === "unknown") return 0.85;
    return 0.6;
  }

  // Photo et print : même famille, mais pas interchangeable avec peinture/dessin.
  if (sameFamily && (artworkFamily === "photography" || artworkFamily === "print")) return sameSupport ? 0.85 : 0.75;

  // Sculpture : matériau/support = cœur de comparaison.
  if (sameFamily && artworkFamily === "sculpture") {
    if (sameSupport) return 0.9;
    if (knownSupportMismatch) return 0.7;
    return 0.8;
  }

  if (sameFamily) return 0.75;

  // Proximités acceptables mais prudentes.
  const paintingVsDrawing = [artworkFamily, rowFamily].includes("painting") && [artworkFamily, rowFamily].includes("drawing");
  if (paintingVsDrawing && (artworkSupport === "paper" || rowSupport === "paper")) return 0.55;

  return 0.35;
}

function conditionTextFromArtwork(artwork) {
  return normalizeText([
    artwork?.condition,
    artwork?.conditionNotes,
    artwork?.condition_report,
    artwork?.conservation_state,
    artwork?.conservation_status,
    artwork?.visual_analysis?.condition,
    artwork?.visual_analysis?.condition_state,
  ].filter(Boolean).join(" "));
}

function conditionTextFromComparable(row) {
  return normalizeText([
    row.condition,
    row.condition_notes,
    row.conditionNotes,
    row.condition_report,
    row.conservation_state,
    row.conservation_status,
    row.comment,
    row.description,
  ].filter(Boolean).join(" "));
}

// Grille méthodologique ARTKIV : applicable A / B / C.
// En AutoCUP, l'état pondère la fiabilité du comparable.
// Pour la méthode à points Groupe C, la décote directe est gérée côté EstimationSemblablesPage.
function conditionCoefficient(artwork, row) {
  const text = conditionTextFromArtwork(artwork) || conditionTextFromComparable(row);

  if (!text) return 1;

  if (/irreparable|irrecuperable|irrécupérable|write.?off/.test(text)) return 0;
  if (/partiellement.?restaurable|partially.?restorable/.test(text)) return 0.35;
  if (/tres.?abime|très.?abime|tres.?abîme|très.?abîme|heavily.?damaged|fortement.?endommage/.test(text)) return 0.5;
  if (/tres.?moyen|très.?moyen|very.?poor|degradation.?notable|dégradation.?notable|restauration.?conseillee|restauration.?conseillée/.test(text)) return 0.7;
  if (/moyen|fair|usure|rayure|pliure|gondol|foxing|rousseur|jaunissement|tache/.test(text)) return 0.8;
  if (/excellent|tres.?bon|très.?bon|very.?good|bon|good|parfait|mint/.test(text)) return 1;

  return 0.9;
}

function proximityCoefficient(artwork, row) {
  const artworkArtist = normalizeText(artwork?.artist);
  const rowArtist = normalizeText(row.artist);

  if (artworkArtist && rowArtist && artworkArtist === rowArtist) return 1;
  if (artworkArtist && rowArtist && rowArtist.includes(artworkArtist)) return 0.85;
  if (row.relationType === "analog" || row.isAnalogComparable || row.is_analog_comparable) return 0.75;
  return 0.55;
}

function freshnessCoefficient(row) {
  const rawDate = row.auction_date || row.date || row.sale_date || row.created_at;
  if (!rawDate) return 0.5;

  const saleDate = new Date(rawDate);
  if (Number.isNaN(saleDate.getTime())) return 0.5;

  const now = new Date();
  const diffDays = (now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24);

  // Date future ou incohérente : conservée comme contexte, mais exclue du calcul pondéré.
  if (diffDays < 0) return 0.25;

  if (diffDays <= 180) return 1;
  if (diffDays <= 365) return 0.9;
  if (diffDays <= 730) return 0.7;
  if (diffDays <= 1095) return 0.5;
  if (diffDays <= 1825) return 0.35;

  return 0.25;
}

function expertDiscountFactor(row) {
  const pct = toNumber(row.discount_pct ?? row.expert_discount_pct ?? row.discountPct ?? row.analogDiscountPct);
  if (pct === null) return 1;

  // Accepte -25 ou 25 pour exprimer une décote de 25 %.
  const normalized = pct > 0 ? -pct : pct;
  return Math.max(0, 1 + normalized / 100);
}

function computeDecisionFactors(artwork, row) {
  const artworkDims = parseArtworkDimensions(artwork);
  const condition = conditionCoefficient(artwork, row);
  const dim = dimensionCoefficient(artworkDims, row);
  const tech = techniqueCoefficient(artwork, row);
  const proximity = proximityCoefficient(artwork, row);
  const freshness = freshnessCoefficient(row);
  const discount = expertDiscountFactor(row);
  const excludedByFreshness = freshness <= 0.25;
  const excludedByCondition = condition <= 0;

  // Les coefficients état / dimension / technique / proximité / fraîcheur pondèrent la fiabilité.
  // Ils ne décotent pas le prix observé. La seule réduction de prix automatique est la décote expert Groupe B.
  const decisionCoefficient = condition * dim * tech * proximity * freshness;
  const effectiveWeight = excludedByFreshness || excludedByCondition ? 0 : Math.max(0.05, decisionCoefficient);

  return {
    conditionCoefficient: condition,
    dimensionCoefficient: dim,
    techniqueCoefficient: tech,
    proximityCoefficient: proximity,
    freshnessCoefficient: freshness,
    decisionCoefficient,
    discountFactor: discount,
    effectiveWeight,
    excludedByFreshness,
    excludedByCondition,
    exclusionReasonKey: excludedByCondition
      ? "autoCUPAssistant.exclusions.condition"
      : excludedByFreshness
      ? "autoCUPAssistant.exclusions.freshness"
      : null,
  };
}


function computeMarketSegment(artwork, row) {
  const isAnalog = row?.relationType === "analog" || row?.isAnalogComparable || row?.is_analog_comparable;
  const tech = toNumber(row?.techniqueCoefficient) ?? techniqueCoefficient(artwork, row);
  const own = !isAnalog;

  if (own && tech >= 0.9) {
    return { key: "primary", multiplier: 1, rank: 1 };
  }

  if (own && tech >= 0.55) {
    return { key: "secondary", multiplier: 0.45, rank: 2 };
  }

  if (own) {
    return { key: "own_context", multiplier: 0.25, rank: 3 };
  }

  if (tech >= 0.75) {
    return { key: "analog_same_segment", multiplier: 0.2, rank: 4 };
  }

  return { key: "analog_context", multiplier: 0.08, rank: 5 };
}


function computeComparableScore(artwork, row) {
  const artworkArtist = normalizeText(artwork?.artist);
  const rowArtist = normalizeText(row.artist);
  const artworkMedium = normalizeText(artwork?.medium || artwork?.medium_category || artwork?.typeOfArt);
  const rowMedium = normalizeText(`${row.medium || ""} ${row.material || ""}`);
  const artworkDims = parseArtworkDimensions(artwork);

  let score = 0;

  if (artworkArtist && rowArtist && artworkArtist === rowArtist) score += 35;
  else if (artworkArtist && rowArtist && rowArtist.includes(artworkArtist)) score += 26;
  else if (row.relationType === "analog") score += 22;
  else score += 8;

  if (artworkMedium && rowMedium && rowMedium.includes(artworkMedium)) score += 20;
  else if (techniqueCoefficient(artwork, row) >= 0.75) score += 14;
  else if (isUniqueWork(artwork) && isMultipleEdition(row)) score -= 15; // pièce unique vs multiple : pénalité
  else score += 8;

  score += dimensionScore(artworkDims, row);

  const condition = conditionCoefficient(artwork, row);
  if (condition >= 1) score += 8;
  else if (condition >= 0.8) score += 5;
  else if (condition >= 0.7) score += 2;
  else if (condition <= 0.5) score -= 10;

  const fresh = freshnessCoefficient(row);
  if (fresh >= 1) score += 12;
  else if (fresh >= 0.65) score += 8;
  else score += 4;

  if (getComparablePrice(row)) score += 8;
  if (isLikelyUnsold(row)) score -= 12;
  if (row.relationType === "analog") score -= 8;

  return Math.max(5, Math.min(100, Math.round(score)));
}

function getMethodGroup(artistQuote) {
  return artistQuote?.method_group || artistQuote?.methodGroup || "direct_cup";
}

function getMinimumRequired(artistQuote) {
  const group = getMethodGroup(artistQuote);
  if (group === "point_method") return 0;
  if (group === "analogy_cup") return 5;
  return 3;
}

function getMinimumLabel(artistQuote, t) {
  const group = getMethodGroup(artistQuote);
  if (group === "analogy_cup") return t("autoCUPAssistant.minimum.groupB");
  if (group === "point_method") return t("autoCUPAssistant.minimum.pointMethod");
  return t("autoCUPAssistant.minimum.groupA");
}

function buildSuggestion(rows, artistQuote = null) {
  const usableRows = rows.filter((r) => !r.excludedByFreshness && !r.excludedByCondition);
  const soldRows = usableRows.filter((r) => !isLikelyUnsold(r) && rawComparablePrice(r));

  const weightedPrices = soldRows
    .map((row) => {
      const raw = rawComparablePrice(row);
      const discount = row.discountFactor ?? expertDiscountFactor(row);
      return {
        value: raw && discount ? raw * discount : raw,
        weight: row.effectiveWeight || row.decisionCoefficient || 1,
      };
    })
    .filter((item) => Number.isFinite(item.value) && item.value > 0);

  const weightedLows = soldRows
    .map((row) => ({
      value: toNumber(row.estimate_low) ? toNumber(row.estimate_low) * (row.discountFactor ?? expertDiscountFactor(row)) : null,
      weight: row.effectiveWeight || row.decisionCoefficient || 1,
    }))
    .filter((item) => Number.isFinite(item.value) && item.value > 0);

  const weightedHighs = soldRows
    .map((row) => ({
      value: toNumber(row.estimate_high) ? toNumber(row.estimate_high) * (row.discountFactor ?? expertDiscountFactor(row)) : null,
      weight: row.effectiveWeight || row.decisionCoefficient || 1,
    }))
    .filter((item) => Number.isFinite(item.value) && item.value > 0);

  const prices = weightedPrices.map((item) => item.value);
  const estimateLows = weightedLows.map((item) => item.value);
  const estimateHighs = weightedHighs.map((item) => item.value);

  if (!prices.length && (!estimateLows.length || !estimateHighs.length)) return null;

  const med = weightedMedian(weightedPrices) || median(prices) || median([...estimateLows, ...estimateHighs]);
  const p25 = weightedPercentile(weightedPrices, 0.25) || percentile(prices, 0.25) || weightedMedian(weightedLows) || median(estimateLows) || med * 0.75;
  const p75 = weightedPercentile(weightedPrices, 0.75) || percentile(prices, 0.75) || weightedMedian(weightedHighs) || median(estimateHighs) || med * 1.25;

  const low = Math.max(0, Math.round(Math.min(p25 * 0.9, med * 0.85) / 100) * 100);
  const high = Math.max(low, Math.round(Math.max(p75 * 1.15, med * 1.35) / 100) * 100);
  const retained = Math.round(med / 100) * 100;

  // Base brute avant tout discount individuel (pour affichage dans FinalMethodologyPanel)
  const rawPrices = soldRows.map((r) => rawComparablePrice(r)).filter((v) => v > 0);
  const rawMed = median(rawPrices) || med;
  const rawP25 = percentile(rawPrices, 0.25) || rawMed * 0.75;
  const rawP75 = percentile(rawPrices, 0.75) || rawMed * 1.25;
  const rawLow = Math.max(0, Math.round(Math.min(rawP25 * 0.9, rawMed * 0.85) / 100) * 100);
  const rawHigh = Math.max(rawLow, Math.round(Math.max(rawP75 * 1.15, rawMed * 1.35) / 100) * 100);
  const rawRetained = Math.round(rawMed / 100) * 100;

  const minimumRequired = getMinimumRequired(artistQuote);
  const canComputePreview = prices.length >= 3 || minimumRequired === 0;
  const canComputeFinal = prices.length >= minimumRequired;

  let confidence = 45;
  if (prices.length >= 3) confidence += 25;
  if (prices.length >= 5) confidence += 10;
  if (getMethodGroup(artistQuote) === "analogy_cup") confidence -= 8;

  const avgWeight = weightedPrices.length
    ? weightedPrices.reduce((sum, item) => sum + item.weight, 0) / weightedPrices.length
    : 0.5;

  if (avgWeight >= 0.75) confidence += 8;
  if (avgWeight < 0.4) confidence -= 10;

  const spread = high && low ? high / Math.max(low, 1) : 2;
  if (spread < 2) confidence += 10;
  if (spread > 3) confidence -= 10;
  if (!canComputeFinal) confidence = Math.min(confidence, 69);

  return {
    low,
    high,
    retained,
    median: med,
    // Base brute avant discountFactor individuel — utilisée par FinalMethodologyPanel
    // pour afficher la fourchette "avant décotes" et calculer la prudence explicitement.
    rawLow: rawLow || low,
    rawHigh: rawHigh || high,
    rawRetained: rawRetained || retained,
    soldCount: prices.length,
    unsoldCount: rows.filter(isLikelyUnsold).length,
    minimumRequired,
    canComputePreview,
    canComputeFinal,
    status: canComputeFinal ? "final" : "provisional",
    confidence: Math.max(20, Math.min(90, Math.round(confidence))),
  };
}


function comparableManualKey(value) {
  if (!value) return "";
  const title = normalizeKey(value.title || "");
  const artist = normalizeKey(value.artist || value.artist_name || "");
  const date = String(value.date || value.auction_date || value.sale_date || "").slice(0, 10);
  const price = Math.round(toNumber(value.price || value.hammer_price || value.estimated_value || value.estimate_high || value.estimate_low) || 0);
  return [artist, title, date, price].join("|");
}

function toManualComparable(row, t) {
  const price = rawComparablePrice(row) || toNumber(row.estimate_low) || "";

  // IMPORTANT — architecture ARTKIV :
  // estimation_comparables = stock source global, souvent sans estimation_id.
  // estimation_selected_comparables = lignes retenues pour CE calcul.
  // On transmet donc l'id source au parent pour qu'il puisse créer la ligne sélectionnée
  // avec estimation_id côté page/formulaire, sans dupliquer ni rattacher artificiellement
  // le comparable source global.
  return {
    comparable_id: row.id || null,
    source_comparable_id: row.id || null,
    estimation_comparable_id: row.id || null,

    title: row.title || t("autoCUPAssistant.common.untitled"),
    artist: row.artist || "",
    source: [row.source === "artprice_pdf" ? "Artprice" : row.source, row.auction_house]
      .filter(Boolean)
      .join(" · "),
    price: price ? String(Math.round(price)) : "",
    selected_price: price ? Math.round(price) : null,
    date: row.auction_date || "",
    auction_date: row.auction_date || null,
    auction_house: row.auction_house || null,
    url: getComparableSourceUrl(row),

    // Champs source conservés pour audit / génération rapport / insertion selected.
    hammer_price: toNumber(row.hammer_price),
    estimate_low: toNumber(row.estimate_low),
    estimate_high: toNumber(row.estimate_high),
    price_currency: row.price_currency || "EUR",
    medium: row.medium || null,
    material: row.material || null,
    dimensions: row.dimensions || null,
    width_cm: toNumber(row.width_cm),
    height_cm: toNumber(row.height_cm),
    depth_cm: toNumber(row.depth_cm),
    source_file: row.source_file || null,
    source_page: row.source_page || null,
    source_blob_path: row.source_blob_path || null,

    marketSegment: row.marketSegment || "",
    marketSegmentLabel: row.marketSegmentLabelKey
      ? t(row.marketSegmentLabelKey)
      : row.marketSegmentLabel || "",
    marketWeightMultiplier: row.marketWeightMultiplier || null,
    techniqueCoefficient: row.techniqueCoefficient || null,
    dimensionCoefficient: row.dimensionCoefficient || null,
    freshnessCoefficient: row.freshnessCoefficient || null,
    proximityCoefficient: row.proximityCoefficient || null,
    conditionCoefficient: row.conditionCoefficient || null,
    effectiveWeight: row.effectiveWeight || null,
    comparableScore: row.comparableScore || null,
  };
}

function getComparableSourceUrl(row = {}) {
  const candidates = [
    row.source_url,
    row.url,
    row.source_file,
    row.link,
    row.external_url,
  ];

  const url = candidates.find((value) => {
    const raw = String(value || "").trim();
    return /^https?:\/\//i.test(raw);
  });

  return url ? String(url).trim() : "";
}

function getComparableSourceLabel(row = {}, t) {
  const sourceText = String(row.source || row.source_type || "").toLowerCase();
  const url = getComparableSourceUrl(row).toLowerCase();

  if (sourceText.includes("artprice") || url.includes("artprice.")) return t("autoCUPAssistant.sources.artprice");
  if (sourceText.includes("invaluable") || url.includes("invaluable.")) return t("autoCUPAssistant.sources.invaluable");
  if (sourceText.includes("artnet") || url.includes("artnet.")) return t("autoCUPAssistant.sources.artnet");
  if (sourceText.includes("sotheby") || url.includes("sothebys.")) return t("autoCUPAssistant.sources.sothebys");
  if (sourceText.includes("christie") || url.includes("christies.")) return t("autoCUPAssistant.sources.christies");
  if (row.auction_house) return t("autoCUPAssistant.sources.named", { name: row.auction_house });
  return t("autoCUPAssistant.sources.generic");
}


function percent(value) {
  const n = toNumber(value);
  if (n === null) return "—";
  return `${Math.round(n * 100)}%`;
}

function analogArtistNames(quote) {
  const names = [];

  if (Array.isArray(quote?.analog_comparables)) {
    quote.analog_comparables.forEach((row) => {
      if (row?.artist) names.push(row.artist);
    });
  }

  if (Array.isArray(quote?.analog_artists)) names.push(...quote.analog_artists);

  return [...new Set(names.map((name) => String(name || "").trim()).filter(Boolean))];
}

function findAnalogConfig(quote, rowArtist) {
  const key = normalizeKey(rowArtist);
  return (quote?.analog_comparables || []).find((item) => normalizeKey(item.artist) === key) || null;
}

function applyAnalogDiscount(row, quote) {
  const config = findAnalogConfig(quote, row.artist);
  const explicitDiscount = toNumber(config?.discount_pct ?? config?.discountPct ?? quote?.analog_discount_pct);
  const ratioUsed = toNumber(config?.ratio_used);
  const quotePriceMid = ((toNumber(quote?.price_low) || 0) + (toNumber(quote?.price_high) || 0)) / 2;
  const comparablePrice = toNumber(row.hammer_price ?? row.estimated_value ?? row.estimate_mid);
  const autoRatio = quotePriceMid && comparablePrice && comparablePrice > quotePriceMid
    ? Math.min(0.95, quotePriceMid / comparablePrice)
    : null;
  const discountFactor = explicitDiscount !== null
    ? Math.max(0, 1 - Math.abs(explicitDiscount) / 100)
    : ratioUsed !== null
    ? Math.max(0.05, Math.min(1, ratioUsed))
    : autoRatio !== null
    ? autoRatio
    : 1;
  const analogDiscountPct = explicitDiscount !== null
    ? Math.round(Math.abs(explicitDiscount))
    : ratioUsed !== null
    ? Math.round((1 - ratioUsed) * 100)
    : 0;
  return {
    ...row,
    relationType: "analog",
    isAnalogComparable: true,
    analogReason: config?.reason || quote?.analogy_reason || null,
    analogSource: config?.source || "",
    analogWeightPct: toNumber(config?.weight_pct),
    analogDiscountPct,
    discountFactor,
  };
}

function buildComparableSummary(row, methodGroup, t, locale) {
  const heading = row.relationType === "analog"
    ? row.analogDiscountPct
      ? t("autoCUPAssistant.summary.analogDiscount", { discount: row.analogDiscountPct })
      : t("autoCUPAssistant.summary.analog")
    : methodGroup === "analogy_cup"
    ? t("autoCUPAssistant.summary.ownGroupB")
    : t("autoCUPAssistant.summary.directGroupA");

  const parts = [
    heading,
    row.auction_house,
    row.auction_date ? formatDate(row.auction_date, locale) : null,
    row.dimensions,
  ].filter(Boolean);
  return parts.join(" · ");
}

export default function AutoCUPAssistant({
  artwork,
  currentUserId,
  userRole,
  userPlan,
  canUseAutoCUP,
  onApplyComparables,
  onMethodologyComparablesChange,
  manualComparables = [],
  maxRows = 8,
  artistQuote: artistQuoteProp = null,
  estimationId,
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.language);
  const [rows, setRows] = useState([]);
  const [artistQuoteInternal, setArtistQuote] = useState(null);
  // La prop externe (transmise par EstimationSemblablesPage) a priorité sur
  // le state interne chargé par AutoCUP lui-même. Cela garantit que
  // buildSuggestion et getMethodGroup reçoivent toujours la bonne cotation.
  const artistQuote = artistQuoteProp ?? artistQuoteInternal;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [artistStockTab, setArtistStockTab] = useState("all");

  const isLocked = !canUseAutoCUP;

  useEffect(() => {
    let cancelled = false;

    async function loadImportedComparables() {
      if (!artwork?.artist || !currentUserId) {
        setRows([]);
        setArtistQuote(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const artist = String(artwork.artist).trim();
        const artistKey = artwork.artist_key || normalizeKey(artist);

        // Recherche cotation : d'abord par artist_key exact, fallback ilike sur artist
        let quoteData = null, quoteError = null;
        ({ data: quoteData, error: quoteError } = await supabase
          .from("artist_quotes")
          .select("*")
          .eq("expert_id", currentUserId)
          .eq("artist_key", artistKey)
          .order("updated_at", { ascending: false, nullsFirst: false })
          .limit(5));

        if (!quoteError && (!quoteData || !quoteData.length)) {
          ({ data: quoteData, error: quoteError } = await supabase
            .from("artist_quotes")
            .select("*")
            .eq("expert_id", currentUserId)
            .ilike("artist", artist)
            .order("updated_at", { ascending: false, nullsFirst: false })
            .limit(5));
        }

        if (quoteError) throw quoteError;

        // Sélection de la cotation la plus pertinente par famille médium + proximité dim
        // (identique à EstimationSemblablesPage — évite de prendre céramique/dessin pour une peinture)
        const artworkDims = parseArtworkDimensions(artwork);
        const artworkDimSum = artworkDims.h && artworkDims.w ? artworkDims.h + artworkDims.w : null;

        const scoreQuoteForArtwork = (q) => {
          let sc = 0;
          const qMedium = normalizeText(q.medium || q.medium_key || q.medium_category || '');
          // Famille médium identique : fort bonus
          const artworkFamily = marketFamilyFromArtwork(artwork);
          const qFamily = primaryMediumFamily(qMedium);
          if (artworkFamily !== 'unknown' && artworkFamily === qFamily) sc += 100;
          // Proximité dimensionnelle
          if (artworkDimSum && q.reference_h_cm && q.reference_w_cm) {
            const qDim = Number(q.reference_h_cm) + Number(q.reference_w_cm);
            const diff = Math.abs(artworkDimSum - qDim) / Math.max(artworkDimSum, qDim);
            sc += Math.round((1 - diff) * 40);
          }
          // Tie-breaker : cotation plus récente prime (updated_at)
          if (q.updated_at) sc += Math.min(10, Math.floor((new Date(q.updated_at) - new Date('2026-01-01')) / 86400000 / 10));
          // Tie-breaker : analogy_cup prime sur direct_cup à score égal
          if (q.method_group === 'analogy_cup') sc += 5;
          if (q.method_group === 'point_method') sc += 3;
          return sc;
        };

        const sortedQuotes = [...(quoteData || [])].sort((a, b) => scoreQuoteForArtwork(b) - scoreQuoteForArtwork(a));
        const quote = sortedQuotes[0] || null;
        const methodGroup = getMethodGroup(quote);
        const names = methodGroup === "analogy_cup" ? [artist, ...analogArtistNames(quote)] : [artist];
        const keys = [...new Set(names.map(normalizeKey).filter(Boolean))];

        const visibilityFilters = [
          `created_by.eq.${currentUserId}`,
          `uploaded_by.eq.${currentUserId}`,
          `parsed_by.eq.${currentUserId}`,
          `owner_id.eq.${artwork.client_id || currentUserId}`,
          `client_id.eq.${artwork.client_id || currentUserId}`,
          // Comparables importés par l'expert pour la collection (expert_client)
          `visibility.eq.expert_client`,
        ];

        if (userRole === "expert") visibilityFilters.push(`expert_id.eq.${currentUserId}`);
        if (["admin", "team"].includes(userRole)) visibilityFilters.push("visibility.eq.team_artkiv", "visibility.eq.shared");

        let query = supabase
          .from("estimation_comparables")
          .select("*")
          .eq("source_type", "externe")
          .or(visibilityFilters.join(","))
          .order("auction_date", { ascending: false, nullsFirst: false })
          // On charge le stock complet de l’artiste : le calcul reste limité par maxRows,
          // mais l’expert peut auditer toutes les auctions importées.
          .limit(1000);

        // Stratégie double : artist_key (avec espaces ET underscores) + ilike sur artist
        // Nécessaire car les imports anciens ont artist_key=null
        // et les formats de clé varient (espaces vs underscores)
        const keysWithUnderscores = keys.map(k => k.replace(/ /g, '_'));
        const allKeys = [...new Set([...keys, ...keysWithUnderscores])].filter(Boolean);

        // IMPORTANT : PostgREST .in.() requiert des guillemets doubles autour des valeurs
        // contenant des espaces, sinon le parsing silencieusement ignore ces valeurs.
        // Ex: artist_key.in.(francoise petrovitch) → cassé
        //     artist_key.in.("francoise petrovitch") → OK
        const quotedKeys = allKeys.map(k => `"${k}"`);

        // Construire les filtres artist ilike pour couvrir artist_key=null
        // On cherche aussi par artist_key partiel (ex: 'othoniel' dans 'jean michel othoniel 1964')
        const artistIlikeFilters = names
          .map(n => {
            const clean = String(n).trim();
            // Extraire le nom court (dernier mot du nom propre) pour un matching plus souple
            const lastName = clean.split(' ').filter(w => w.length > 3).pop() || clean;
            return `artist.ilike.%${clean}%,artist_key.ilike.%${lastName}%`;
          })
          .join(',');

        if (allKeys.length) {
          // Chercher par artist_key OU par nom artiste (pour les imports sans artist_key)
          // Les deux clauses sont séparées pour éviter les conflits de parsing PostgREST
          // entre .in.() avec guillemets et les filtres ilike.
          query = query.or(
            `artist_key.in.(${quotedKeys.join(',')}),${artistIlikeFilters}`
          );
        } else {
          const artistSearch = String(artist).trim().replace(/\s+/g, ' ');
          query = query.ilike("artist", `%${artistSearch}%`);
        }

        const { data, error: queryError } = await query;
        if (queryError) throw queryError;

        const ownArtistKey = normalizeKey(artist);
        const scored = (data || [])
          .filter((row) => row.artwork_id !== artwork.id)
          .map((row) => {
            const rowKey = row.artist_key || normalizeKey(row.artist);
            const isOwn = rowKey === ownArtistKey || normalizeText(row.artist) === normalizeText(artist);
            const prepared = isOwn ? { ...row, relationType: "own" } : applyAnalogDiscount(row, quote);
            const decision = computeDecisionFactors(artwork, prepared);
            const segment = computeMarketSegment(artwork, { ...prepared, ...decision });
            const segmentAdjustedWeight = decision.effectiveWeight * segment.multiplier;

            return {
              ...prepared,
              ...decision,
              marketSegment: segment.key,
              marketSegmentLabelKey: `autoCUPAssistant.marketSegments.${segment.key}`,
              marketSegmentRank: segment.rank,
              marketWeightMultiplier: segment.multiplier,
              effectiveWeight: segmentAdjustedWeight,
              comparableScore: clampScore(computeComparableScore(artwork, { ...prepared, ...decision }) + Math.max(0, 8 - segment.rank * 2)),
            };
          })
          .sort((a, b) => {
            if (a.excludedByCondition !== b.excludedByCondition) return a.excludedByCondition ? 1 : -1;
            if (a.excludedByFreshness !== b.excludedByFreshness) return a.excludedByFreshness ? 1 : -1;
            return b.comparableScore - a.comparableScore;
          });

        if (!cancelled) {
          setArtistQuote(quote);
          setRows(scored);
          console.log('[AutoCUP] rows chargés:', { total: scored.length, own: scored.filter(r => r.relationType === 'own').length, analog: scored.filter(r => r.relationType === 'analog').length, excluded_freshness: scored.filter(r => r.excludedByFreshness).length, artist_key: artwork?.artist_key || artwork?.artist, sample_artist_keys: scored.slice(0,3).map(r => ({artist: r.artist, artist_key: r.artist_key, relationType: r.relationType, freshness: r.freshnessCoefficient})) });
        }
      } catch (e) {
        console.error("Erreur AutoCUPAssistant:", e);
        if (!cancelled) setError(e.message || t("autoCUPAssistant.errors.load"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadImportedComparables();
    return () => {
      cancelled = true;
    };
  }, [artwork, currentUserId, userRole, t]);

  const selectedRows = useMemo(() => {
    const usable = rows.filter(
      (row) => !row.excludedByCondition && isAutoCUPEligible(artwork, row)
    );

    if (getMethodGroup(artistQuote) !== "analogy_cup") {
      return usable.slice(0, maxRows);
    }

    const own = usable.filter((row) => row.relationType !== "analog");
    const analog = usable.filter((row) => row.relationType === "analog");

    const primary = own.filter((row) => row.marketSegment === "primary");
    const secondary = own.filter((row) => row.marketSegment !== "primary");
    const analogSame = analog.filter((row) => row.marketSegment === "analog_same_segment");
    const analogContext = analog.filter((row) => row.marketSegment !== "analog_same_segment");

    const ownTarget = analog.length ? Math.min(5, maxRows - 2) : maxRows;
    const analogTarget = Math.max(0, maxRows - ownTarget);

    const pickedOwn = [...primary, ...secondary].slice(0, ownTarget);
    const pickedAnalog = [...analogSame, ...analogContext].slice(0, analogTarget);
    const combined = [...pickedOwn, ...pickedAnalog];

    if (combined.length < maxRows) {
      const used = new Set(combined.map((row) => row.id));
      combined.push(...usable.filter((row) => !used.has(row.id)).slice(0, maxRows - combined.length));
    }

    return combined.slice(0, maxRows);
  }, [rows, maxRows, artistQuote, artwork]);
  const ownArtistStockRows = useMemo(() => {
    const targetArtistKey = normalizeKey(artwork?.artist);
    return rows.filter((row) => {
      const rowArtistKey = normalizeKey(row?.artist_key || row?.artist);
      return row.relationType !== "analog" && targetArtistKey && rowArtistKey === targetArtistKey;
    });
  }, [rows, artwork?.artist]);

  const stockTabRows = useMemo(() => {
    const sameTechniqueRows = ownArtistStockRows.filter((row) => row.marketSegment === "primary");
    const sameFamilyRows = ownArtistStockRows.filter((row) => row.marketSegment === "secondary" || row.marketSegment === "own_context");
    const pricedRows = ownArtistStockRows.filter((row) => rawComparablePrice(row));
    const estimatesOnlyRows = ownArtistStockRows.filter((row) => !rawComparablePrice(row) && (toNumber(row.estimate_low) || toNumber(row.estimate_high)));

    if (artistStockTab === "same_technique") return sameTechniqueRows;
    if (artistStockTab === "same_family") return sameFamilyRows;
    if (artistStockTab === "priced") return pricedRows;
    if (artistStockTab === "estimates") return estimatesOnlyRows;
    return ownArtistStockRows;
  }, [ownArtistStockRows, artistStockTab]);

  const artistStockTabs = useMemo(() => {
    const sameTechniqueCount = ownArtistStockRows.filter((row) => row.marketSegment === "primary").length;
    const sameFamilyCount = ownArtistStockRows.filter((row) => row.marketSegment === "secondary" || row.marketSegment === "own_context").length;
    const pricedCount = ownArtistStockRows.filter((row) => rawComparablePrice(row)).length;
    const estimatesCount = ownArtistStockRows.filter((row) => !rawComparablePrice(row) && (toNumber(row.estimate_low) || toNumber(row.estimate_high))).length;

    return [
      { key: "all", label: t("autoCUPAssistant.stock.tabs.all"), count: ownArtistStockRows.length },
      { key: "same_technique", label: t("autoCUPAssistant.stock.tabs.sameTechnique"), count: sameTechniqueCount },
      { key: "same_family", label: t("autoCUPAssistant.stock.tabs.sameFamily"), count: sameFamilyCount },
      { key: "priced", label: t("autoCUPAssistant.stock.tabs.priced"), count: pricedCount },
      { key: "estimates", label: t("autoCUPAssistant.stock.tabs.estimates"), count: estimatesCount },
    ];
  }, [ownArtistStockRows, t]);

  const manualComparableKeys = useMemo(() => {
    return new Set((manualComparables || []).map(comparableManualKey).filter(Boolean));
  }, [manualComparables]);

  const addStockRowToManualComparables = (row) => {
    const mapped = toManualComparable(row, t);
    onApplyComparables?.([mapped], {
      appendComparable: true,
      methodGroup: getMethodGroup(artistQuote),
      source: "artist_stock_tab",
      selectedComparableIds: mapped.comparable_id ? [mapped.comparable_id] : [],
      selectedComparableRows: [mapped],
    });
  };

  const soldSelectedCount = useMemo(
    () => selectedRows.filter((row) => !row.excludedByFreshness && !row.excludedByCondition && !isLikelyUnsold(row) && rawComparablePrice(row)).length,
    [selectedRows]
  );
  const analogCount = useMemo(() => selectedRows.filter((row) => row.relationType === "analog").length, [selectedRows]);
  const primaryCount = useMemo(() => selectedRows.filter((row) => row.marketSegment === "primary").length, [selectedRows]);
  const secondaryCount = useMemo(() => selectedRows.filter((row) => row.marketSegment === "secondary" || row.marketSegment === "own_context").length, [selectedRows]);
  const analogSameSegmentCount = useMemo(() => selectedRows.filter((row) => row.marketSegment === "analog_same_segment").length, [selectedRows]);
  const minimumRequired = getMinimumRequired(artistQuote);
  const suggestion = useMemo(() => buildSuggestion(selectedRows, artistQuote), [selectedRows, artistQuote]);
  const canShowPreview = Boolean(suggestion?.canComputePreview);
  const canApplySuggestion = Boolean(suggestion && canShowPreview);

  useEffect(() => {
    if (typeof onMethodologyComparablesChange !== "function") return;

    const methodologyRows = selectedRows
      .filter((row) => !row.excludedByFreshness && !row.excludedByCondition && (rawComparablePrice(row) || toNumber(row.estimate_low) || toNumber(row.estimate_high)))
      .slice(0, maxRows)
      .map((row) => toManualComparable(row, t));

    const ownRows = selectedRows.filter((r) => r.relationType === 'own' && !r.excludedByFreshness && rawComparablePrice(r));
    const hasOwnRecentFromAutoCUP = ownRows.length > 0;
    const hasPrimarySegmentFromAutoCUP = ownRows.some((r) => r.marketSegment === 'primary');

    onMethodologyComparablesChange(methodologyRows, {
      ...suggestion,
      minimumRequired,
      currentComparableCount: soldSelectedCount,
      canComputeFinal: Boolean(suggestion?.canComputeFinal),
      canComputePreview: Boolean(suggestion?.canComputePreview),
      methodGroup: getMethodGroup(artistQuote),
      selectedComparableIds: methodologyRows.map((row) => row.comparable_id).filter(Boolean),
      selectedComparableRows: methodologyRows,
      hasOwnRecentFromAutoCUP,
      hasPrimarySegmentFromAutoCUP,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRows,
    suggestion,
    // onMethodologyComparablesChange volontairement exclu des deps :
    // la page recrée cette fonction à chaque render (inline arrow), ce qui
    // provoquerait une boucle infinie si elle était incluse. Le callback
    // est stable en pratique — il lit uniquement des setters React (stables).
    maxRows,
    minimumRequired,
    soldSelectedCount,
    artistQuote,
    t,
  ]);

  const applyTopComparables = () => {
    const top = selectedRows
      .filter((row) => !row.excludedByFreshness && !row.excludedByCondition && (rawComparablePrice(row) || toNumber(row.estimate_low)))
      .slice(0, Math.max(5, minimumRequired))
      .map((row) => toManualComparable(row, t));

    if (!top.length) return;
    onApplyComparables?.(top, {
      ...suggestion,
      minimumRequired,
      currentComparableCount: soldSelectedCount,
      canComputeFinal: Boolean(suggestion?.canComputeFinal),
      canComputePreview: Boolean(suggestion?.canComputePreview),
      methodGroup: getMethodGroup(artistQuote),

      // Données explicites pour le parent : la page doit créer/mettre à jour
      // estimation_selected_comparables avec estimation_id + comparable_id.
      // Ne pas faire d'UPDATE estimation_comparables.estimation_id sur les sources globales.
      selectedComparableIds: top.map((row) => row.comparable_id).filter(Boolean),
      selectedComparableRows: top,
    });
  };

  return (
    <div className={`relative mb-8 ${isLocked ? "opacity-95" : ""}`}>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">
                {t("autoCUPAssistant.header.eyebrow")}
              </p>
              <h3 className="text-xl font-serif text-gray-900">
                {t("autoCUPAssistant.header.title")}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {t("autoCUPAssistant.header.description")}
              </p>
            </div>
            <span
              className={`text-xs px-3 py-1 rounded-full ${
                isLocked
                  ? "bg-amber-100 text-amber-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {isLocked
                ? t("autoCUPAssistant.header.premiumOnly")
                : t("autoCUPAssistant.header.active")}
            </span>
          </div>
        </div>

        <div className="p-6">
          {loading && (
            <p className="text-sm text-gray-500">
              {t("autoCUPAssistant.loading")}
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && !error && artistQuote && (
            <div className="mb-4 border border-gray-100 bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
              {t("autoCUPAssistant.quote.loaded")}:{" "}
              <strong>
                {getMethodGroupLabel(getMethodGroup(artistQuote), t)}
              </strong>
              {artistQuote.quote_date
                ? ` · ${t("autoCUPAssistant.quote.date", {
                    date: formatDate(artistQuote.quote_date, locale),
                  })}`
                : ""}
              {getMethodGroup(artistQuote) === "analogy_cup" &&
                analogArtistNames(artistQuote).length > 0 && (
                  <span>
                    {" · "}
                    {t("autoCUPAssistant.quote.analogies", {
                      names: analogArtistNames(artistQuote).join(", "),
                    })}
                  </span>
                )}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="border border-dashed border-gray-300 p-6 text-center bg-gray-50 rounded">
              <p className="text-sm text-gray-700 mb-3">
                {t("autoCUPAssistant.empty.title")}
              </p>
              <p className="text-xs text-gray-400 mb-5">
                {t("autoCUPAssistant.empty.description")}
              </p>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/expert/import-artprice?artist=${encodeURIComponent(
                      artwork?.artist || ""
                    )}&artworkId=${encodeURIComponent(artwork?.id || "")}`
                  )
                }
                className="inline-flex items-center gap-2 bg-black text-white px-5 py-2 text-sm hover:bg-gray-800 transition"
              >
                📥 {t("autoCUPAssistant.empty.import")}
              </button>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5">
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t("autoCUPAssistant.metrics.comparables")}
                  </p>
                  <p className="text-lg font-semibold">
                    {t("autoCUPAssistant.metrics.minimum", {
                      current: soldSelectedCount,
                      minimum: minimumRequired,
                    })}
                  </p>
                  {getMethodGroup(artistQuote) === "analogy_cup" && (
                    <p className="text-[11px] text-gray-400">
                      {t("autoCUPAssistant.metrics.breakdown", {
                        primary: primaryCount,
                        secondary: secondaryCount,
                        analog: analogCount,
                        sameSegment: analogSameSegmentCount,
                      })}
                    </p>
                  )}
                </div>
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t("autoCUPAssistant.metrics.calculationStatus")}
                  </p>
                  <p
                    className={`text-lg font-semibold ${
                      suggestion?.canComputeFinal
                        ? "text-green-700"
                        : "text-amber-700"
                    }`}
                  >
                    {suggestion?.canComputeFinal
                      ? t("autoCUPAssistant.status.finalizable")
                      : canShowPreview
                      ? t("autoCUPAssistant.status.provisional")
                      : t("autoCUPAssistant.status.insufficient")}
                  </p>
                </div>
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t("autoCUPAssistant.metrics.weightedMedian")}
                  </p>
                  <p className="text-lg font-semibold">
                    {canShowPreview
                      ? formatEuro(suggestion?.median, locale)
                      : "—"}
                  </p>
                </div>
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t("autoCUPAssistant.metrics.proposedRange")}
                  </p>
                  <p className="text-lg font-semibold">
                    {canShowPreview && suggestion
                      ? `${formatEuro(suggestion.low, locale)} – ${formatEuro(
                          suggestion.high,
                          locale
                        )}`
                      : "—"}
                  </p>
                </div>
                <div className="border border-gray-200 rounded p-3 bg-gray-50">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400">
                    {t("autoCUPAssistant.metrics.confidence")}
                  </p>
                  <p className="text-lg font-semibold">
                    {canShowPreview ? suggestion?.confidence : "—"}/100
                  </p>
                </div>
              </div>

              {!suggestion?.canComputeFinal && canShowPreview && (
                <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                  {t("autoCUPAssistant.notices.provisional", {
                    count: soldSelectedCount,
                    minimum: getMinimumLabel(artistQuote, t),
                  })}
                </div>
              )}

              {!canShowPreview && (
                <div className="mb-4 border border-blue-100 bg-blue-50 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
                  {t("autoCUPAssistant.notices.insufficient", {
                    minimum: getMinimumLabel(artistQuote, t),
                  })}
                </div>
              )}

              <div className="mb-4 border border-blue-100 bg-blue-50 rounded-lg p-3 text-xs text-blue-900 leading-relaxed">
                {t("autoCUPAssistant.notices.marketStructure")}
              </div>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mb-5">
                {selectedRows.map((row) => (
                  <div
                    key={row.id}
                    className={`p-4 flex items-start justify-between gap-4 ${
                      row.relationType === "analog" ? "bg-purple-50/50" : ""
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">
                          {row.title || t("autoCUPAssistant.common.untitled")}
                        </p>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full ${
                            row.relationType === "analog"
                              ? "bg-purple-100 text-purple-800"
                              : getMethodGroup(artistQuote) === "analogy_cup"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {row.relationType === "analog"
                            ? t("autoCUPAssistant.badges.analogyB")
                            : getMethodGroup(artistQuote) === "analogy_cup"
                            ? t("autoCUPAssistant.badges.ownGroupB")
                            : t("autoCUPAssistant.badges.directGroupA")}
                        </span>
                      </div>
                      {(() => {
                        const tech = comparableTechniqueParts(row, t);
                        return (
                          <>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {[
                                row.artist,
                                tech.family,
                                row.dimensions ||
                                  `${row.height_cm || "?"} × ${
                                    row.width_cm || "?"
                                  } cm`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {t("autoCUPAssistant.row.technique")}: {" "}
                              <span className="font-medium text-gray-700">
                                {tech.material || "—"}
                              </span>
                              {tech.support && (
                                <span className="ml-2 text-[11px] text-gray-400">
                                  {t("autoCUPAssistant.row.supportDriver", {
                                    support: tech.support,
                                  })}
                                </span>
                              )}
                            </p>
                          </>
                        );
                      })()}
                      <p className="text-xs text-gray-400 mt-1">
                        {[
                          row.auction_house,
                          row.auction_date
                            ? formatDate(row.auction_date, locale)
                            : null,
                          row.source_file,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-2">
                        {t("autoCUPAssistant.row.decision", {
                          condition: percent(row.conditionCoefficient),
                          dimensions: percent(row.dimensionCoefficient),
                          technique: percent(row.techniqueCoefficient),
                          proximity: percent(row.proximityCoefficient),
                          freshness: percent(row.freshnessCoefficient),
                          weight: percent(row.effectiveWeight),
                        })}
                      </p>
                      {(row.excludedByFreshness || row.excludedByCondition) && (
                        <p className="text-[11px] text-amber-800 mt-1 font-medium">
                          {row.exclusionReasonKey
                            ? t(row.exclusionReasonKey)
                            : ""}
                        </p>
                      )}
                      {row.relationType === "analog" && (
                        <p className="text-[11px] text-purple-700 mt-1">
                          {buildComparableSummary(
                            row,
                            getMethodGroup(artistQuote),
                            t,
                            locale
                          )}
                          {row.analogReason
                            ? ` · ${row.analogReason}`
                            : ` · ${t("autoCUPAssistant.analog.reasonFallback")}`}
                        </p>
                      )}
                      {row.relationType !== "analog" &&
                        getMethodGroup(artistQuote) === "analogy_cup" && (
                          <p className="text-[11px] text-amber-700 mt-1">
                            {buildComparableSummary(
                              row,
                              getMethodGroup(artistQuote),
                              t,
                              locale
                            )}
                          </p>
                        )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatEuro(rawComparablePrice(row), locale)}
                      </p>
                      {row.discountFactor !== 1 && rawComparablePrice(row) && (
                        <p className="text-xs text-gray-400">
                          {t("autoCUPAssistant.row.afterDiscount", {
                            value: formatEuro(
                              rawComparablePrice(row) * row.discountFactor,
                              locale
                            ),
                          })}
                        </p>
                      )}
                      {(row.estimate_low || row.estimate_high) && (
                        <p className="text-xs text-gray-400">
                          {t("autoCUPAssistant.row.estimateRange", {
                            low: formatEuro(row.estimate_low, locale),
                            high: formatEuro(row.estimate_high, locale),
                          })}
                        </p>
                      )}
                      <p className="text-[11px] mt-1 text-amber-700">
                        {t("autoCUPAssistant.row.match", {
                          score: clampScore(row.comparableScore),
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {ownArtistStockRows.length > selectedRows.length && (
                <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="p-4 border-b border-gray-100 bg-gray-50">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-400">
                          {t("autoCUPAssistant.stock.eyebrow")}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {t("autoCUPAssistant.stock.found", {
                            count: ownArtistStockRows.length,
                            artist:
                              artwork?.artist ||
                              t("autoCUPAssistant.common.thisArtist"),
                          })}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t("autoCUPAssistant.stock.auditHelp")}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {artistStockTabs.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setArtistStockTab(tab.key)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition ${
                            artistStockTab === tab.key
                              ? "bg-black text-white border-black"
                              : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="divide-y divide-gray-100 max-h-[520px] overflow-auto">
                    {stockTabRows.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">
                        {t("autoCUPAssistant.stock.empty")}
                      </p>
                    ) : (
                      stockTabRows.map((row) => {
                        const retained = rawComparablePrice(row);
                        const alreadySelected = selectedRows.some(
                          (selected) => selected.id === row.id
                        );
                        const alreadyInManualForm = manualComparableKeys.has(
                          comparableManualKey(toManualComparable(row, t))
                        );
                        return (
                          <div
                            key={row.id || `${row.title}-${row.auction_date}`}
                            className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
                          >
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-sm text-gray-900">
                                  {row.title ||
                                    t("autoCUPAssistant.common.untitled")}
                                </p>
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                  {row.marketSegmentLabelKey
                                    ? t(row.marketSegmentLabelKey)
                                    : t(
                                        "autoCUPAssistant.marketSegments.unclassified"
                                      )}
                                </span>
                                {alreadySelected && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                                    {t("autoCUPAssistant.stock.autoSelected")}
                                  </span>
                                )}
                                {alreadyInManualForm && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                                    {t("autoCUPAssistant.stock.addedToForm")}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {[
                                  row.auction_house,
                                  row.auction_date
                                    ? formatDate(row.auction_date, locale)
                                    : null,
                                  row.dimensions,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              {getComparableSourceUrl(row) && (
                                <p className="text-xs mt-1">
                                  <a
                                    href={getComparableSourceUrl(row)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {getComparableSourceLabel(row, t)} ↗
                                  </a>
                                </p>
                              )}
                              {(() => {
                                const tech = comparableTechniqueParts(row, t);
                                return (
                                  <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                    <p>
                                      {t("autoCUPAssistant.stock.family")}: {" "}
                                      <span className="font-medium text-gray-700">
                                        {tech.family || "—"}
                                      </span>
                                    </p>
                                    <p>
                                      {t("autoCUPAssistant.stock.technique")}: {" "}
                                      <span className="font-medium text-gray-700">
                                        {tech.material || "—"}
                                      </span>
                                    </p>
                                    {tech.support && (
                                      <p className="text-[11px] text-gray-400">
                                        {t(
                                          "autoCUPAssistant.stock.supportDriver",
                                          { support: tech.support }
                                        )}
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                              {row.decisionText && (
                                <p className="text-xs text-gray-600 mt-2">
                                  {row.decisionText}
                                </p>
                              )}
                            </div>
                            <div className="md:text-right shrink-0">
                              <p className="text-sm font-semibold text-gray-900">
                                {retained
                                  ? formatEuro(retained, locale)
                                  : t("autoCUPAssistant.stock.priceUnpublished")}
                              </p>
                              {(row.estimate_low || row.estimate_high) && (
                                <p className="text-xs text-gray-400">
                                  {t("autoCUPAssistant.row.estimateRange", {
                                    low: formatEuro(row.estimate_low, locale),
                                    high: formatEuro(row.estimate_high, locale),
                                  })}
                                </p>
                              )}
                              <p className="text-[11px] text-amber-700 mt-1">
                                {t("autoCUPAssistant.row.match", {
                                  score: clampScore(row.comparableScore),
                                })}
                              </p>
                              <button
                                type="button"
                                onClick={() => addStockRowToManualComparables(row)}
                                disabled={isLocked || alreadyInManualForm}
                                className="mt-2 px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {alreadyInManualForm
                                  ? t("autoCUPAssistant.stock.alreadyAdded")
                                  : t("autoCUPAssistant.stock.addToForm")}
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  {suggestion?.canComputeFinal
                    ? t("autoCUPAssistant.suggestion.finalizable")
                    : t("autoCUPAssistant.suggestion.provisional")} {" "}
                  {t("autoCUPAssistant.suggestion.expertControl")}
                </p>
                <button
                  type="button"
                  onClick={applyTopComparables}
                  disabled={isLocked || !canApplySuggestion}
                  className="px-5 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {t("autoCUPAssistant.suggestion.apply")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isLocked && (
        <div className="absolute inset-0 rounded-lg bg-white/70 backdrop-blur-[1px] flex items-center justify-center p-6">
          <div className="max-w-md bg-white border border-amber-200 shadow-lg rounded-lg p-5 text-center">
            <p className="text-sm font-semibold text-gray-900 mb-1">
              {t("autoCUPAssistant.locked.title")}
            </p>
            <p className="text-xs text-gray-600 leading-relaxed">
              {t("autoCUPAssistant.locked.description")}
            </p>
          </div>
        </div>
      )}
    </div>
  );

}

// --- ARTKIV market segmentation patch ---
export function computeAdvancedComparableWeight(targetArtwork, comparable) {
  const sameArtist =
    normalizeKey(targetArtwork?.artist) === normalizeKey(comparable?.artist);

  const techniqueWeight = getTechniqueMarketWeight(targetArtwork, comparable);

  const careerMarketWeight = getCareerMarketWeight(
    targetArtwork?.artist_career_stage || "mid-career",
    comparable?.artist_career_stage || "mid-career",
    sameArtist
  );

  const analogPenalty =
    comparable?.relationType === "analog" ? 0.35 : 1;

  return Number(
    (
      techniqueWeight *
      careerMarketWeight *
      analogPenalty
    ).toFixed(3)
  );
}